/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import api from '@/api';

export const namespaced = true;

function emptySiteAssessment() {
  return {
    background: {
      preparerId: null,
      preparedDate: null,
      landownerInformation: null,
      historicalUse: null,
      currentUse: null,
      generalConditions: null,
      watershedContext: null,
    },
    goals: {
      landownerGoals: null,
      forestManagementGoals: null,
      agriculturalManagementGoals: null,
      resourcePriorities: null,
      forestResourceConcerns: null,
      managementConcerns: null,
      otherConcerns: null,
    },
    propertyOverview: {
      topography: null,
      soilsAndGeology: null,
      waterResources: null,
      waterRights: null,
      vegetationDescription: null,
      wildlifeObservations: null,
      habitatValue: null,
      culturalResources: null,
      invasiveSpecies: null,
      regulatoryCompliance: null,
    },
    resourceAnalysis: {
      recommendations: null,
      additionalResource: null,
    },
    status: 'Started',
  };
}
function getInvasivesText(invasiveDetails, rootState) {
  const { invasives } = rootState.SiteVisit;
  return invasives.fields
    .filter((field) => invasiveDetails[field.field]
      && invasiveDetails[field.field].percentOfInfestation !== null)
    .map((field) => `${invasiveDetails[field.field].percentOfInfestation} (${invasiveDetails[field.field].infestationSize} sq ft) infested by ${field.name}`)
    .join(', ');
}
function defaultNAString(value) {
  return value || 'N/A';
}
function liveStockString(value, animal) {
  if (value > 0) {
    return `${value} ${animal} <br/>`;
  }
  return '';
}
function defaultLivestock(Cattle, Horses, Chickens, Sheep, Turkeys, Goats, Alpacas) {
  if (!Cattle && !Horses && !Chickens && !Sheep && !Turkeys && !Goats && !Alpacas) {
    return 'N/A';
  }
  return '';
}
function buildHistoricPlantCommunityString(siteVisit) {
  if (siteVisit.habitatDetails.historicPlantType
    && siteVisit.habitatDetails.historicPlantType.length > 0) {
    return `${siteVisit.habitatDetails.historicPlantType.join(', ')} - ${defaultNAString(siteVisit.habitatDetails.historicPlantCondition)}<br/>`;
  }
  return 'N/A<br/>';
}
function initializeSiteAssessmentFromVisit(siteVisit, rootState) {
  const siteAssessment = emptySiteAssessment();
  siteAssessment.siteId = siteVisit.siteId;
  siteAssessment.background.preparedDate = new Date();
  siteAssessment.background.historicalUse = `Historic plant community: ${buildHistoricPlantCommunityString(siteVisit)}
Brief history of the property: ${defaultNAString(siteVisit.urbanDetails.briefHistory)}<br/>`;
  siteAssessment.background.currentUse = `Current site management: ${defaultNAString(siteVisit.habitatDetails.currentSiteManagement)}<br/>
Fertilizer schedule in use: ${defaultNAString(siteVisit.agriculturalDetails.fertilizerScheduleDescription)}<br/>
Types of fertilizer in use: ${defaultNAString(siteVisit.agriculturalDetails.fertilizerTypeDescription)}<br/>
Livestock manure  management in use: ${defaultNAString(siteVisit.agriculturalDetails.manureManagementDescription)}<br/>
Livestock raised on site:
${defaultLivestock(
    siteVisit.agriculturalDetails.livestockRaisedOnSiteCattle,
    siteVisit.agriculturalDetails.livestockRaisedOnSiteHorses,
    siteVisit.agriculturalDetails.livestockRaisedOnSiteChickens,
    siteVisit.agriculturalDetails.livestockRaisedOnSiteSheep,
    siteVisit.agriculturalDetails.livestockRaisedOnSiteTurkeys,
    siteVisit.agriculturalDetails.livestockRaisedOnSiteAlpacas,
  )}<br/>
  ${liveStockString(siteVisit.agriculturalDetails.livestockRaisedOnSiteCattle, 'Cattle')}
  ${liveStockString(siteVisit.agriculturalDetails.livestockRaisedOnSiteHorses, 'Horses')}
  ${liveStockString(siteVisit.agriculturalDetails.livestockRaisedOnSiteChickens, 'Chickens')}
  ${liveStockString(siteVisit.agriculturalDetails.livestockRaisedOnSiteSheep, 'Sheep')}
  ${liveStockString(siteVisit.agriculturalDetails.livestockRaisedOnSiteTurkeys, 'Turkeys')}
  ${liveStockString(siteVisit.agriculturalDetails.livestockRaisedOnSiteGoats, 'Goats')}
  ${liveStockString(siteVisit.agriculturalDetails.livestockRaisedOnSiteAlpacas, 'Alpacas')}
Irrigation Methods: ${defaultNAString(siteVisit.agriculturalDetails.irrigationMethods.join(', '))}<br/>
Farming Method: ${defaultNAString(siteVisit.agriculturalDetails.farmingMethod)}<br/>
Crops being grown: ${defaultNAString(siteVisit.agriculturalDetails.cropsBeingGrown.join(', '))}<br/>
Acres of forest: ${defaultNAString(siteVisit.forestDetails.acresOfForest)}`;
  siteAssessment.goals.landownerGoals = `Landowner goals: ${defaultNAString(siteVisit.generalDetails.landownerGoals)}<br/>
Habitat/Streams conservation goals: ${defaultNAString(siteVisit.habitatDetails.conservationGoals.join(', '))}<br/>
Urban conservation goals: ${defaultNAString(siteVisit.urbanDetails.primaryGoals.join(', '))}<br/>`;
  siteAssessment.goals.forestManagementGoals = `Forest management goals: ${defaultNAString(siteVisit.forestDetails.primaryGoals.join(', '))}<br/>
Other goals: ${defaultNAString(siteVisit.forestDetails.otherGoals)}`;
  siteAssessment.goals.agriculturalManagementGoals = `Agricultural management goals: ${defaultNAString(siteVisit.agriculturalDetails.agriculturalManagementGoals.join(', '))}<br/>
Other goals: ${defaultNAString(siteVisit.agriculturalDetails.otherAgriculturalManagementGoals)}`;
  siteAssessment.goals.resourcePriorities = `Landowner resource priorities: ${defaultNAString(siteVisit.generalDetails.landownerResourcePriorities.join(', '))}`;
  siteAssessment.goals.forestResourceConcerns = `Forest resource concerns: ${defaultNAString(siteVisit.forestDetails.forestConcerns.join(', '))}<br/>
Other concerns: ${defaultNAString(siteVisit.forestDetails.otherConcerns)}`;
  if (siteVisit.streamsDetails) {
    siteAssessment.propertyOverview.waterResources = `Historic hydrology: ${defaultNAString(siteVisit.habitatDetails.historicHydrology.join(', '))}<br/>
Streambank conditions: ${defaultNAString(siteVisit.streamsDetails.streamBankConditions.join(', '))}<br/>
Stream channel observations: ${defaultNAString(siteVisit.streamsDetails.channelObservations.join(', '))}<br/>
Visible passage barriers: ${defaultNAString(siteVisit.streamsDetails.visiblePassageBarriers)}<br/>
Dominate substrate: ${defaultNAString(siteVisit.streamsDetails.dominantSubstrate.join(', '))}<br/>
Potential for creating or allowing for natural hydrologic processes? ${defaultNAString(siteVisit.streamsDetails.naturalProcesses)}`;
  }
  siteAssessment.propertyOverview.waterRights = `Water Rights: ${defaultNAString(siteVisit.generalDetails.waterRights.join(', '))}`;
  siteAssessment.propertyOverview.vegetationDescription = `Native species present: ${defaultNAString(siteVisit.habitatDetails.nativeRiparianSpp.join(', '))}<br/>
Historic plant community type: ${defaultNAString(siteVisit.habitatDetails.historicPlantType.join(', '))}<br/>
History plant community condition: ${defaultNAString(siteVisit.habitatDetails.historicPlantCondition)}<br/>
Forest stands less than 30 years old and overstocked with a single species? ${defaultNAString(siteVisit.habitatDetails.forestStands)}`;
  siteAssessment.propertyOverview.wildlifeObservations = `Wildlife observations: ${defaultNAString(siteVisit.habitatDetails.wildlifeObservations)}<br/>
Wildlife concerns: ${defaultNAString(siteVisit.habitatDetails.wildlifeConcerns.join(', '))}<br/>
Opportunities for beaver influence: ${defaultNAString(siteVisit.habitatDetails.beaverInfluence)}`;
  siteAssessment.propertyOverview.invasiveSpecies = `Adjacent property has > 75% target weed spp: ${defaultNAString(siteVisit.habitatDetails.targetWeedSpp)}, ${getInvasivesText(siteVisit.invasiveSpeciesDetails, rootState)}`;
  return siteAssessment;
}

export const state = {
  siteAssessment: null,
  siteAssessmentsPendingApproval: [],
  tualatinContacts: [],
  serverError: null,
  submitSuccess: false,
};

export const actions = {
  async fetchOrCreateSiteAssessment({ commit, rootState }, { currentSiteVisit }) {
    if (!state.siteAssessment || state.siteAssessment.siteId !== currentSiteVisit.siteId) {
      return api.get(`/siteassessment/${currentSiteVisit.siteId}`)
        .then((response) => commit('SET_SITE_ASSESSMENT', response.data))
        .catch(() => commit('SET_SITE_ASSESSMENT', initializeSiteAssessmentFromVisit(currentSiteVisit, rootState)));
    }
    return commit('SET_SITE_ASSESSMENT', state.siteAssessment);
  },
  async saveAssessment({ commit }, siteAssessment) {
    commit('SET_SERVER_ERROR', null);
    await api.post('/siteAssessment/', siteAssessment)
      .then((response) => commit('SET_SITE_ASSESSMENT', response.data))
      .catch((error) => {
        console.error(error);
        commit('SET_SERVER_ERROR', error);
      });
  },
  async fetchExistingSiteAssessment({ commit }, siteId) {
    if (!state.siteAssessment || state.siteAssessment.siteId !== siteId) {
      return api.get(`/siteassessment/${siteId}`)
        .then((response) => commit('SET_SITE_ASSESSMENT', response.data))
        .catch(() => commit('SET_SITE_ASSESSMENT', null));
    }
    return commit('SET_SITE_ASSESSMENT', state.siteAssessment);
  },
  async fetchTualatinContacts({ commit }) {
    return api.get('/contact')
      .then((response) => commit('SET_CONTACTS', response.data));
  },
  async getSiteAssessmentsPendingApproval({ commit }) {
    await api.get(
      '/siteassessment/pendingapproval',
    ).then(
      async (response) => commit('SET_SITE_ASSESSMENTS_PENDING_APPROVAL', response.data),
    );
  },
  setSubmitSuccess({ commit }, value) {
    return commit('SET_SUBMIT_SUCCESS', value);
  },
};

export const mutations = {
  SET_SITE_ASSESSMENT(state, siteAssessment) {
    state.siteAssessment = siteAssessment;
  },
  SET_SERVER_ERROR(state, error) {
    state.serverError = error;
  },
  SET_CONTACTS(state, contacts) {
    state.tualatinContacts = contacts;
  },
  SET_SITE_ASSESSMENTS_PENDING_APPROVAL(state, data) {
    state.siteAssessmentsPendingApproval = data;
  },
  SET_SUBMIT_SUCCESS(state, value) {
    state.submitSuccess = value;
  },
};
